import React from 'react';
import { Link } from 'react-router-dom';

import './css/ProcessingPricing.css'

import { trialPathFor } from './contact'
import {
	CORES_PER_STEP,
	PROCESSING_EXCLUDED,
	PROCESSING_INCLUDED,
	UNLIMITED_STEP,
	YEARLY_FREE_MONTHS,
	describeProcessingPlan,
	formatCores,
	formatPrice,
	isUnlimitedStep,
	monthlyPriceAtStep,
	yearlyPriceAtStep,
} from './pricingTiers'

///@brief   Width of the slider's thumb, which the stylesheet repeats: the
///         filled part of the track has to end under the thumb's centre, and
///         the thumb's centre travels the track's width less its own.
const THUMB_PX: number = 18


// The pipeline without the mark, the pricing page's second part under its
// own title: a slider for the cores it runs on, from sixteen to unlimited,
// the price for them, and the button to ask for it. Quieter than the tiers:
// one panel on the ground rather than cards on a surface.
export function ProcessingPricing(): React.JSX.Element {

	const [ step, setStep ] = React.useState<number>( 1 )

	const cores: string = formatCores( step )

	// The filled share of the track, handed to the stylesheet.
	const fill: number = ( step - 1 ) / ( UNLIMITED_STEP - 1 )
	const rangeStyle: React.CSSProperties = { '--fill': fill, '--thumb': `${ THUMB_PX }px` } as React.CSSProperties

	const onChange = ( event: React.ChangeEvent<HTMLInputElement> ): void => {
		setStep( Number( event.target.value ) )
	}

	return (
		<div className="ProcessingPricing" data-fade >

			<div className="ProcessingPricing-Text" >
				<div className="Page-Text fs-16" >
					Resize, crop, blur, convert and deliver, on your own servers. A core is
					one CPU core of the machine it runs on, processing one image at a time:
					the more cores, the more images at once.
				</div>
				<ul className="ProcessingPricing-Included" >
					{ PROCESSING_INCLUDED.map( ( item: string ): React.JSX.Element => (
						<li className="ProcessingPricing-Included-Item" key={ item } >{ item }</li>
					) ) }
				</ul>
				<div className="ProcessingPricing-Excluded fs-14" >{ PROCESSING_EXCLUDED }</div>
			</div>

			<div className="ProcessingPricing-Calculator" >

				<div className="ProcessingPricing-Slider" >
					<label className="ProcessingPricing-Slider-Label" htmlFor="processing-cores" >
						<span>Cores</span>
						<span className="ProcessingPricing-Slider-Value" >{ cores }</span>
					</label>
					<input
						id="processing-cores"
						className="ProcessingPricing-Range"
						type="range"
						min={ 1 }
						max={ UNLIMITED_STEP }
						step={ 1 }
						value={ step }
						onChange={ onChange }
						style={ rangeStyle }
						aria-valuetext={ isUnlimitedStep( step ) ? 'Unlimited cores' : `${ cores } cores` }
					/>
					<div className="ProcessingPricing-Slider-Marks" aria-hidden="true" >
						<span>{ CORES_PER_STEP } cores</span>
						<span>Unlimited</span>
					</div>
				</div>

				<div className="ProcessingPricing-Prices" >
					<div className="ProcessingPricing-Price" >
						<span className="ProcessingPricing-Amount" >{ formatPrice( yearlyPriceAtStep( step ) ) }</span>
						<span className="ProcessingPricing-Period" >/ year</span>
						<span className="ProcessingPricing-Badge" >{ YEARLY_FREE_MONTHS } months free</span>
					</div>
					<div className="ProcessingPricing-Monthly" >
						or { formatPrice( monthlyPriceAtStep( step ) ) } / month, billed monthly
					</div>
				</div>

				{ /* The request that follows names the cores chosen here. */ }
				<Link className="ProcessingPricing-Cta" to={ trialPathFor( describeProcessingPlan( step ) ) } >
					Request this plan
				</Link>

			</div>

		</div>
	)
}
